import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProduct, createOrder } from "../api";
import { unitPriceFor, kc } from "../pricing";

const PACKETA_LIB = "https://widget.packeta.com/v6/www/js/library.js";
// klíč primárně z /api/product (čerstvý fetch), fallback na runtime-config
const envPacketaKey = () => (window.env && window.env.PACKETA_API_KEY) || "";

// Zásilkovna widget library načteme líně (jen když je potřeba).
let _packetaLoading = null;
function loadPacketa() {
  if (window.Packeta && window.Packeta.Widget) return Promise.resolve();
  if (_packetaLoading) return _packetaLoading;
  _packetaLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = PACKETA_LIB;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { _packetaLoading = null; reject(new Error("Nepodařilo se načíst widget Zásilkovny.")); };
    document.head.appendChild(s);
  });
  return _packetaLoading;
}

export default function ProductPage() {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);

  // počty párů per velikost: { variantId: qty }
  const [quantities, setQuantities] = useState({});
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    delivery_method: "PERSONAL", pickup_point_id: "", pickup_point_name: "",
    payment_method: "CASH", note: "",
  });
  const [pickupError, setPickupError] = useState(null);
  const [tip, setTip] = useState(0);          // dýško pro Evu a Ditu (default 0 = nic)
  const [tipCustom, setTipCustom] = useState(false);
  const [tipCustomVal, setTipCustomVal] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    getProduct().then(setProduct).catch((e) => setError(e.message));
  }, []);

  const totalQty = useMemo(
    () => Object.values(quantities).reduce((s, n) => s + (Number(n) || 0), 0),
    [quantities]
  );
  const shipping = form.delivery_method === "ZASILKOVNA" ? 69 : 0;
  const unitPrice = useMemo(
    () => (product && totalQty ? unitPriceFor(product.price_tiers, totalQty) : 0),
    [product, totalQty]
  );
  const subtotal = unitPrice * totalQty;
  const total = subtotal + shipping + (Number(tip) || 0);

  function setQ(variantId, n, maxStock) {
    // prázdné pole necháme prázdné (ať jde mazat a psát nové číslo)
    if (n === "" || n === null || n === undefined) {
      setQuantities((q) => ({ ...q, [variantId]: "" }));
      return;
    }
    const v = Math.max(0, Math.min(Number(n) || 0, maxStock));
    setQuantities((q) => ({ ...q, [variantId]: v }));
  }

  function setField(k, v) {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // CASH jen u osobního předání
      if (k === "delivery_method" && v === "ZASILKOVNA" && f.payment_method === "CASH") {
        next.payment_method = "BANK_TRANSFER";
      }
      return next;
    });
  }

  async function openPacketa() {
    setPickupError(null);
    const apiKey = (product && product.packeta_api_key) || envPacketaKey();
    if (!apiKey) {
      setPickupError("Výběr výdejního místa zatím není nastavený. Napiš pobočku do poznámky, ozveme se.");
      return;
    }
    try {
      await loadPacketa();
    } catch (err) {
      setPickupError(err.message);
      return;
    }
    window.Packeta.Widget.pick(apiKey, (point) => {
      if (!point) return; // zavřeno bez výběru
      setForm((f) => ({
        ...f,
        pickup_point_id: String(point.id),
        pickup_point_name: point.name || point.formatedValue || point.place || "",
      }));
    }, { language: "cs", country: "cz" });
  }

  async function submit(e) {
    e.preventDefault();
    setFormError(null);
    const items = Object.entries(quantities)
      .filter(([, n]) => Number(n) > 0)
      .map(([variant_id, quantity]) => ({ variant_id: Number(variant_id), quantity: Number(quantity) }));
    if (!items.length) { setFormError("Vyber aspoň jednu velikost a počet."); return; }
    // validace kontaktu (stejně jako BE) — hned zpětná vazba
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((form.email || "").trim())) {
      setFormError("Zadej platný e-mail (např. jmeno@domena.cz)."); return;
    }
    const phoneClean = (form.phone || "").replace(/[\s\-()./]/g, "");
    if (phoneClean && !/^(\+\d{9,15}|\d{9}|420\d{9})$/.test(phoneClean)) {
      setFormError("Telefon zadej jako 9 číslic nebo +420123456789."); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items,
        customer: { name: form.name, email: form.email, phone: form.phone },
        delivery_method: form.delivery_method,
        payment_method: form.payment_method,
        tip: Number(tip) || 0,
        customer_note: form.note || undefined,
      };
      if (form.delivery_method === "ZASILKOVNA") {
        payload.pickup_point_id = form.pickup_point_id;
        if (form.pickup_point_name) payload.pickup_point_name = form.pickup_point_name;
      }
      const order = await createOrder(payload);
      navigate("/objednavka/potvrzeni", { state: { order } });
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (error) return <div className="card error">Chyba načtení produktu: {error}</div>;
  if (!product) return <div className="card">Načítám…</div>;

  return (
    <div className="product">
      <section className="card product-hero">
        <div className="gallery">
          {product.photos && product.photos.length
            ? product.photos.map((src, i) => <img key={i} src={src} alt={product.name} />)
            : <div className="gallery-placeholder">📷 Fotky brzy</div>}
        </div>
        <div className="product-body">
          <h1>{product.name}</h1>
          <table className="tiers">
            <tbody>
              {product.price_tiers.map((t) => (
                <tr key={t.min_quantity}>
                  <td>{t.min_quantity}+ párů</td><td className="num">{kc(t.unit_price)} Kč / pár</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <form className="card order-form" onSubmit={submit}>
        <h2>Objednávka</h2>

        <div className="size-rows">
          {product.variants.map((v) => (
            <div key={v.id} className={`size-row ${(quantities[v.id] || 0) > 0 ? "on" : ""} ${v.in_stock ? "" : "out"}`}>
              <div className="size-label">{v.label}
                <small>{v.in_stock ? `skladem ${kc(v.stock)}` : "vyprodáno"}</small>
              </div>
              <div className="size-qty">
                <button type="button" className="step" disabled={!v.in_stock || !(quantities[v.id] > 0)}
                        onClick={() => setQ(v.id, (quantities[v.id] || 0) - 1, v.stock)}>−</button>
                <input type="number" min="0" max={v.stock} inputMode="numeric" placeholder="0"
                       value={quantities[v.id] ?? ""} disabled={!v.in_stock}
                       onChange={(e) => setQ(v.id, e.target.value, v.stock)} />
                <button type="button" className="step" disabled={!v.in_stock || (quantities[v.id] || 0) >= v.stock}
                        onClick={() => setQ(v.id, (quantities[v.id] || 0) + 1, v.stock)}>+</button>
              </div>
            </div>
          ))}
        </div>
        <p className="muted small">Celkem párů: <strong>{kc(totalQty)}</strong>{totalQty > 0 && ` · ${kc(unitPrice)} Kč/pár`}</p>

        <div className="grid2">
          <label>Jméno
            <input value={form.name} required autoComplete="name"
                   onChange={(e) => setField("name", e.target.value)} />
          </label>
          <label>E-mail
            <input type="email" value={form.email} required autoComplete="email"
                   inputMode="email" onChange={(e) => setField("email", e.target.value)} />
          </label>
        </div>
        <label>Telefon
          <input type="tel" value={form.phone} autoComplete="tel" placeholder="+420 123 456 789"
                 inputMode="tel" onChange={(e) => setField("phone", e.target.value)} />
          <small className="muted">9 číslic nebo mezinárodně s +420. U Zásilkovny je telefon potřeba.</small>
        </label>

        <div className="fieldset-row">
        <fieldset>
          <legend>Doručení</legend>
          <label className="radio"><input type="radio" name="d" checked={form.delivery_method === "PERSONAL"}
            onChange={() => setField("delivery_method", "PERSONAL")} /> Vyzvednu si osobně — budeme vás kontaktovat, domluvíme se (zdarma)</label>
          <label className="radio"><input type="radio" name="d" checked={form.delivery_method === "ZASILKOVNA"}
            onChange={() => setField("delivery_method", "ZASILKOVNA")} /> Zásilkovna — výdejna nebo Z-Box (69 Kč)</label>
          {form.delivery_method === "ZASILKOVNA" && (
            <div className="pickup-box">
              <button type="button" className="btn-pickup" onClick={openPacketa}>
                {form.pickup_point_name ? "Změnit výdejní místo" : "Vybrat výdejní místo Zásilkovna"}
              </button>
              {form.pickup_point_name && (
                <div className="pickup-selected">📍 {form.pickup_point_name}</div>
              )}
              {pickupError && <div className="error small">{pickupError}</div>}
            </div>
          )}
        </fieldset>

        <fieldset>
          <legend>Platba</legend>
          <label className="radio"><input type="radio" name="p" checked={form.payment_method === "BANK_TRANSFER"}
            onChange={() => setField("payment_method", "BANK_TRANSFER")} /> Bankovní převod</label>
          <label className="radio">
            <input type="radio" name="p" checked={form.payment_method === "CASH"}
              disabled={form.delivery_method !== "PERSONAL"}
              onChange={() => setField("payment_method", "CASH")} /> Hotově (jen osobně)
          </label>
        </fieldset>
        </div>

        <div className="order-note">
          <textarea className="note-field" rows="2" value={form.note} maxLength={1000}
                    placeholder="Poznámka k objednávce"
                    onChange={(e) => setField("note", e.target.value)} />
        </div>

        <div className="tip-box">
          <p className="small muted">💛 Přidat dýško pro Evu a Ditu? Dobrovolné, díky za podporu.</p>
          <div className="tip-chips">
            {[1, 5].map((v) => (
              <button type="button" key={v} className={`tip-chip ${!tipCustom && tip === v ? "on" : ""}`}
                      onClick={() => { setTipCustom(false); setTip(v); }}>{v} Kč</button>
            ))}
            <button type="button" className={`tip-chip ${tipCustom ? "on" : ""}`}
                    onClick={() => { setTipCustom(true); setTip(Number(tipCustomVal) || 0); }}>jiné</button>
            {tip > 0 && (
              <button type="button" className="tip-chip clear"
                      onClick={() => { setTipCustom(false); setTip(0); }}>× zrušit</button>
            )}
          </div>
          {tipCustom && (
            <input type="number" min="0" inputMode="numeric" placeholder="0" value={tipCustomVal}
                   onChange={(e) => {
                     // prázdné pole necháme prázdné (ať jde mazat a psát nové číslo) — vzor setQ
                     const raw = e.target.value;
                     if (raw === "") { setTipCustomVal(""); setTip(0); return; }
                     const x = Math.max(0, Number(raw) || 0);
                     setTipCustomVal(x); setTip(x);
                   }} />
          )}
        </div>

        <div className="summary">
          {product.variants.filter((v) => (quantities[v.id] || 0) > 0).map((v) => (
            <div key={v.id} className="item-row">
              <span>vel. {v.label} — {quantities[v.id]}× {kc(unitPrice)} Kč</span>
              <span>{kc(quantities[v.id] * unitPrice)} Kč</span>
            </div>
          ))}
          <div><span>Mezisoučet ({kc(totalQty)} párů)</span><span>{kc(subtotal)} Kč</span></div>
          <div><span>Doprava</span><span>{kc(shipping)} Kč</span></div>
          {tip > 0 && <div><span>Dýško pro Evu a Ditu</span><span>{kc(tip)} Kč</span></div>}
          <div className="total"><span>Celkem</span><span>{kc(total)} Kč</span></div>
        </div>

        {formError && <div className="error">{formError}</div>}
        <button type="submit" className="btn-block" disabled={submitting}>
          {submitting ? "Odesílám…" : `Objednat za ${kc(total)} Kč`}
        </button>
      </form>
    </div>
  );
}
